/** Strings owned by `src/manage/subscriptions/` — "Beheer › Abonnementen"
 * (SUBSCRIPTIONS-FRONTEND-BRIEFING.md § 2): provider configuration, product
 * mapping, scheduled price changes, per-user support actions and webhook
 * URLs. Admin-only screens, separate from `src/features/subscription/`
 * (the caller's own subscription). */
export const manageSubscriptions = {
  config: {
    title: 'Provider configuration',
    lead: 'Credentials and webhook settings per provider — changed live, no deploy.',
    providerApple: 'Apple',
    providerGoogle: 'Google',
    providerMollie: 'Mollie',
    fieldLabel_issuer_id: 'Issuer ID',
    fieldLabel_key_id: 'Key ID',
    fieldLabel_p8_private_key: 'Private key (.p8)',
    fieldLabel_bundle_id: 'Bundle ID',
    fieldLabel_environment: 'Environment',
    fieldLabel_service_account_json: 'Service account JSON',
    fieldLabel_package_name: 'Package name',
    fieldLabel_pubsub_audience: 'Pub/Sub audience',
    fieldLabel_pubsub_service_account_email: 'Pub/Sub service account email',
    fieldLabel_api_key_test: 'API key (test)',
    fieldLabel_api_key_live: 'API key (live)',
    fieldLabel_active_environment: 'Active environment',
    fieldLabel_webhook_signing_secret: 'Webhook signing secret',
    fieldLabel_redirect_url: 'Redirect URL',
    fieldLabel_cancel_url: 'Cancel URL',
    secretHint: 'Stored value: {{masked}}. Leave blank to keep it.',
    noValueYet: 'Not set yet.',
    updatedByBefore: 'Last changed by',
    updatedByAfter: 'on {{when}}',
    save: 'Save',
    saved: 'Configuration saved.',
    testConnection: 'Test connection',
    testResultOk: 'Connection OK: {{message}}',
    testResultFailed: 'Connection failed: {{message}}',
    selectPlaceholder: 'Choose one…',
  },
  products: {
    title: 'Products & providers',
    lead: 'The tier/period/price mapping per provider product — the only place the API knows how long a period is or what it costs.',
    provider: 'Provider',
    tier: 'Tier',
    period: 'Period',
    price: 'Price',
    active: 'Active',
    pending: 'Scheduled',
    edit: 'Edit',
    newProduct: 'New product',
    empty: 'No products mapped for this provider yet.',
    formTitle: 'Product mapping',
    formTitleNew: 'New product mapping',
    intro:
      'This links one provider product id to what it means: which access (tier), for how long, and at what price. This is the only place that meaning is recorded — a purchase with an unknown product id (or with "Active" off) is rejected. This sets the price now.',
    fieldProviderProductId: 'Provider product ID',
    fieldProviderProductIdHint_apple:
      'The exact product id from App Store Connect (e.g. com.nolatr.sub.yearly). Must match exactly, or the purchase is rejected.',
    fieldProviderProductIdHint_google:
      'The exact product/base-plan id from the Play Console. Must match exactly, or the purchase is rejected.',
    fieldProviderProductIdHint_mollie:
      'A self-chosen code — in practice monthly or yearly, since those are the only two values checkout accepts.',
    fieldTier: 'Tier',
    fieldTierHint:
      'Free text — the name of the access level the user gets (e.g. premium). Use the exact same spelling for every product that should grant the same access, matching what the app/Dashboard expects.',
    durationGroupTitle: 'Duration',
    durationGroupHint:
      'How long one purchase of this product grants access — determines the expiry date after purchase, independent of what the store itself shows.',
    fieldPeriodAmount: 'Period amount',
    fieldPeriodUnit: 'Period unit',
    periodUnit_day: 'Day(s)',
    periodUnit_week: 'Week(s)',
    periodUnit_month: 'Month(s)',
    periodUnit_year: 'Year(s)',
    priceGroupTitle: 'Price',
    priceGroupHint_mollie: 'Must be correct: this amount is really charged via Mollie.',
    priceGroupHint_store:
      'Informational only — the real price and billing run through Apple/Google themselves. This field only controls what gets displayed.',
    fieldPriceAmount: 'Price amount',
    fieldPriceCurrency: 'Price currency',
    fieldMollieInterval: 'Mollie interval',
    fieldMollieIntervalHint:
      'Optional — leave blank to fall back to a sensible default (1 month / 12 months). Only fill in for a non-standard interval, in Mollie’s own format.',
    fieldActive: 'Active',
    fieldActiveHint:
      'Turning this off deletes nothing: existing purchases stay visible. New purchases with this product id are then rejected and it disappears from the product list.',
    save: 'Save',
    saved: 'Product saved.',
  },
  userLedger: {
    title: 'User ledger',
    lead: 'Look up a user to see their entitlement, purchases and Mollie subscription, and act on their behalf.',
    searchLabel: 'Search user (name or email)',
    searchPlaceholder: 'Type a name or email address…',
    searchNoResults: 'No users found.',
    manualEntryInstead: 'Already know the User ID? Enter it directly.',
    searchInstead: 'Search by name or email instead?',
    moveSelectedHint: 'Selected: {{name}}',
    lookupLabel: 'User ID',
    lookupSubmit: 'Look up',
    entitlementTitle: 'Entitlement',
    active: 'Active',
    inactive: 'Inactive',
    tier: 'Tier',
    expiresAt: 'Expires',
    purchasesTitle: 'Purchases',
    purchasesEmpty: 'No purchases on this ledger.',
    date: 'Date',
    period: 'Period',
    channel: 'Channel',
    state: 'State',
    actions: 'Actions',
    mollieTitle: 'Mollie subscription',
    mollieNone: 'No Mollie subscription for this user.',
    mollieStatus: 'Status',
    mollieFailedPayments: 'Failed payments',
    syncStatus: 'Sync status',
    synced: 'Status synced.',
    grantTitle: 'Grant complimentary access',
    grantFieldTier: 'Tier',
    grantFieldAmount: 'Duration amount',
    grantFieldUnit: 'Duration unit',
    grantFieldReason: 'Reason (optional)',
    grantSubmit: 'Grant access',
    granted: 'Access granted.',
    recompute: 'Recalculate entitlement',
    recomputed: 'Entitlement recalculated.',
    cancelMollie: 'Cancel Mollie subscription',
    cancelled: 'Cancelled — access until {{date}}.',
    revoke: 'Revoke',
    revoked: 'Purchase revoked.',
    move: 'Move',
    moveFieldToUserId: 'Destination user ID',
    moveSubmit: 'Move purchase',
    moved: 'Purchase moved.',
  },
  webhooks: {
    title: 'Webhook URLs',
    copy: 'Copy',
    copied: 'Copied.',
    key_mollie_classic: 'Mollie (classic)',
    key_mollie_next_gen: 'Mollie (next-gen)',
    key_apple_production: 'Apple (production)',
    key_apple_sandbox: 'Apple (sandbox)',
    key_google_pubsub: 'Google (Pub/Sub)',
  },
  errors: {
    unknown_provider: 'Unknown provider.',
    unknown_config_key: 'One of these fields is not valid for this provider.',
    invalid_provider: 'Unknown provider.',
    invalid_tier: 'This tier is not valid.',
    invalid_period_amount: 'This duration amount is not valid.',
    invalid_period_unit: 'This duration unit is not valid.',
    no_active_subscription: 'This user has no active Mollie subscription.',
    purchase_not_found: 'This purchase could not be found.',
    already_on_account: 'This purchase is already on that account.',
    invalid_user: 'That user could not be found.',
    subscription_not_found: 'This subscription could not be found.',
    subscription_cancelled: 'This subscription is already cancelled.',
    mollie_unreachable: 'Mollie could not be reached — try again shortly.',
  },
} as const;
